import { useCallback, useRef } from 'react';

import useDebouncedCallback from '../../../common/hooks/useDebouncedCallback';
import { DomainMetadataUpdateItem } from '../DomainGraphUtils';

function getMetadataItemKey(item: DomainMetadataUpdateItem) {
	return `${item.targetType}:${item.id}`;
}

export function useDomainMetadataWriteQueue(onFlush: (items: DomainMetadataUpdateItem[]) => Promise<void>, delay = 350) {
	const pendingItemsRef = useRef(new Map<string, DomainMetadataUpdateItem>());

	const flushPendingItems = useCallback(async () => {
		const items = Array.from(pendingItemsRef.current.values());
		if (items.length === 0) {
			return;
		}

		pendingItemsRef.current.clear();
		await onFlush(items);
	}, [onFlush]);

	const debouncedFlush = useDebouncedCallback(() => {
		void flushPendingItems();
	}, delay);

	const queueItems = useCallback(
		(items: DomainMetadataUpdateItem[]) => {
			items.forEach((item) => pendingItemsRef.current.set(getMetadataItemKey(item), item));
			debouncedFlush();
		},
		[debouncedFlush],
	);

	const flushItems = useCallback(
		async (items: DomainMetadataUpdateItem[] = []) => {
			items.forEach((item) => pendingItemsRef.current.set(getMetadataItemKey(item), item));
			await flushPendingItems();
		},
		[flushPendingItems],
	);

	return {
		queueItems,
		flushItems,
	};
}
